import React from "react";
import { money } from "@/lib/financeApi";
import { Section } from "@/components/nds/windows/finance/payables/Parts1";
import { PayLandlordButton } from "@/components/nds/windows/finance/flow/PayLandlord";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";

export function Checks({ rows, win, total }) {
  return (
    <Section n="3" t="Checks already written" sub={`${win.from} → ${win.to} · ${money(total)} taken off what's owed`} testid="lp-checks">
      <div className="max-h-56 overflow-auto">{rows.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 text-[11px] border-t border-white/5 py-1" data-testid={`lp-check-${c.id}`}>
          <span className="font-mono w-20">{c.date}</span><span className="font-mono w-24 text-right text-nex-silver">{money(c.amount)}</span>
          <span className="text-nex-muted">{c.method}{c.check_number ? ` #${c.check_number}` : ""}</span>
          <span className="text-nex-muted flex-1 min-w-0 break-words">{c.covers ? `covers ${c.covers} · ` : ""}{c.tied_to}</span>
        </div>))}
        {!rows.length && <div className="text-xs text-nex-muted">No checks in this window.</div>}</div>
    </Section>
  );
}

export function Reimbursements({ rows, total }) {
  return (
    <Section n="4" t="Reimbursements owed back to us" sub={`${money(total)} comes off the next landlord check`} testid="lp-reimb">
      {rows.map((x) => (
        <div key={x.id} className="flex items-center gap-2 text-[11px] border-t border-white/5 py-1" data-testid={`lp-reimb-${x.id}`}>
          <span className="font-mono w-20">{x.date}</span><span className="flex-1 text-nex-muted">{x.description} · {x.status}</span><span className="font-mono">{money(x.amount)}</span>
        </div>))}
      {!rows.length && <div className="text-xs text-nex-muted">Nothing owed back right now. Add reimbursables on the landlord's Vendors page.</div>}
    </Section>
  );
}

export function FundingLog({ rows, wc, onChanged }) {
  const sa = useIsSuper();
  return (
    <Section n="5" t="Funding log" sub="invoices marked funded, with the net landlord check due after checks and reimbursements" testid="lp-log">
      <div className="p-3 rounded-sm border border-nex-gold/30 bg-nex-gold/[0.04] flex flex-wrap items-center gap-3 text-xs" data-testid="lp-write-check">
        <span className="text-nex-silver">{wc.landlord}</span>
        <span className="text-nex-muted font-mono">{money(wc.gross)} funded rent − {money(wc.less_checks)} checks − {money(wc.less_reimbursements)} reimbursements</span>
        <span className="font-mono text-nex-gold" data-testid="lp-net-due">= {money(wc.net)} to write</span>
        {wc.ahead > 0 && <span className="text-emerald-300" data-testid="lp-ahead">Checks are ahead of funded rent by {money(wc.ahead)}</span>}
        {sa && <span className="ml-auto"><PayLandlordButton testid="lp-write-check-btn" label="Write check" onChanged={onChanged}
          prefill={{ amount: wc.net > 0 ? wc.net.toFixed(2) : "", vendor_id: wc.landlord_id || "", memo: "Net landlord check · Invoice Funding & Landlord Payables" }} /></span>}
      </div>
      <div className="max-h-64 overflow-auto">{rows.map((f) => (
        <div key={`${f.id}-${f.received_on}`} className="flex flex-wrap items-center gap-2 text-[11px] border-t border-white/5 py-1" data-testid={`lp-log-${f.number}`}>
          <span className="font-mono w-20">{f.received_on}</span><span className="font-mono text-nex-silver">#{f.number}</span><span className="text-nex-muted">{f.employer}</span>
          <span className="font-mono">{money(f.received)} received</span><span className="font-mono text-nex-muted">rent {money(f.rent)}</span>
          <span className={`ml-auto font-mono ${f.net_due_after > 0 ? "text-nex-gold" : "text-emerald-300"}`}>net due after: {f.net_due_after < 0 ? `ahead ${money(-f.net_due_after)}` : money(f.net_due_after)}</span>
        </div>))}
        {!rows.length && <div className="text-xs text-nex-muted">No invoices marked funded yet.</div>}</div>
    </Section>
  );
}
